import { useState, type ReactNode } from 'react';
import { subscriptionMirrorAlias, type ModelProvider } from '@reflex/shared';
import type {
  ProviderSecretManageAffordance,
  ProviderSubscriptionAffordance,
} from '@reflex/ui/components/model-providers/ModelProviderSecretPicker';
import { RegisterClaudeMaxDialog } from './RegisterClaudeMaxDialog';
import { RegisterCodexDialog } from './RegisterCodexDialog';

/**
 * Which affordance produced a write. `register` is a new subscription the user
 * asked for in this gesture, so a caller may adopt it. `update` re-pastes an
 * existing credential and says nothing about which subscription the surface
 * should be on — adopting it would switch a run to a credential the user only
 * meant to repair.
 */
export type ProviderSubscriptionWriteMode = 'register' | 'update';

export interface ProviderSubscriptionAffordanceOptions {
  /**
   * What to do with the subscription the user just wrote, given its `mps_*` id
   * and which affordance wrote it. Surfaces that hold a selection in local
   * state pass their setter; a running agent passes its switch handler, so
   * registering from inside the picker applies the new subscription to the run
   * as one user-initiated action. Callers must branch on `mode` before adopting
   * the id (see {@link ProviderSubscriptionWriteMode}).
   *
   * Called with `null` when the server could not mirror the credential into a
   * selectable row — the credential is registered, but there is nothing to act
   * on, so a caller that writes should treat null as "do nothing".
   */
  onRegistered?: (
    modelProviderSecretId: string | null,
    mode: ProviderSubscriptionWriteMode,
  ) => void;
}

/** Cog tooltip / accessible name on a subscription row. */
const MANAGE_LABEL = 'Update this subscription';

/**
 * Wire the picker's `addSubscription` and `manageSubscription` affordances to
 * the appropriate paste-JSON dialog for the current provider:
 *
 *   - `anthropic` → {@link RegisterClaudeMaxDialog}
 *   - `openai`    → {@link RegisterCodexDialog}
 *   - any other   → no affordance
 *
 * Subscriptions are user-tier only — every consumer of the picker that
 * passes `allowedTypes` including `'subscription'` can opt into this
 * helper to surface a footer affordance inside the popover. The caller
 * mounts the returned `dialog` adjacent to the picker; the dialog owns
 * its own mutation state and refreshes the picker's accessible-secrets
 * list on success so the new credential appears automatically.
 *
 * One dialog serves both affordances: `add` opens it in register mode, while
 * `manage` pins it to the credential behind the row whose cog was clicked, so a
 * subscription whose token went stale can be re-pasted without leaving the
 * surface. The alias to update is recovered from the mirror row's stored name
 * with {@link subscriptionMirrorAlias} — Codex is single-subscription, so its
 * dialog always writes the one credential and needs no alias.
 *
 * Appearing in the list is not the same as being *chosen*, so pass
 * {@link ProviderSubscriptionAffordanceOptions.onRegistered} to say what should
 * happen to the subscription that was written. It fires for updates too — with
 * `mode: 'update'`, because a repair is not a choice: only the caller knows
 * whether the credential that was re-pasted is the one its surface is on.
 */
export function useProviderSubscriptionAffordance(
  provider: ModelProvider,
  { onRegistered }: ProviderSubscriptionAffordanceOptions = {},
): {
  affordance: ProviderSubscriptionAffordance | undefined;
  manage: ProviderSecretManageAffordance | undefined;
  dialog: ReactNode;
} {
  const [open, setOpen] = useState(false);
  // Which credential the dialog is pinned to: `undefined` registers a new
  // subscription, a set alias updates that existing one in place. Kept across
  // close so the dialog doesn't flip its copy mid-dismiss; the next open always
  // sets it.
  const [alias, setAlias] = useState<string | undefined>(undefined);
  // Which affordance opened the dialog, reported back with the write so a
  // caller can tell "the user chose this subscription" from "the user repaired
  // some subscription". Codex has no alias to pin, so this is the only thing
  // separating its two entry points.
  const [mode, setMode] = useState<ProviderSubscriptionWriteMode>('register');

  function openRegister() {
    setAlias(undefined);
    setMode('register');
    setOpen(true);
  }

  function openUpdate(target?: string) {
    setAlias(target);
    setMode('update');
    setOpen(true);
  }

  function handleWritten(modelProviderSecretId: string | null) {
    onRegistered?.(modelProviderSecretId, mode);
  }

  if (provider === 'anthropic') {
    return {
      affordance: {
        label: 'Add Claude Max subscription…',
        onAdd: openRegister,
      },
      manage: {
        label: MANAGE_LABEL,
        onManage: (secret) => openUpdate(subscriptionMirrorAlias('claude-max', secret.name)),
      },
      dialog: (
        <RegisterClaudeMaxDialog
          open={open}
          onOpenChange={setOpen}
          onRegistered={handleWritten}
          alias={alias}
        />
      ),
    };
  }
  if (provider === 'openai') {
    return {
      affordance: {
        label: 'Add Codex subscription…',
        onAdd: openRegister,
      },
      // Codex holds a single subscription, so registering over it *is* the
      // update — the dialog takes no alias and the cog opens the same form.
      manage: {
        label: MANAGE_LABEL,
        onManage: () => openUpdate(),
      },
      dialog: (
        <RegisterCodexDialog open={open} onOpenChange={setOpen} onRegistered={handleWritten} />
      ),
    };
  }
  return { affordance: undefined, manage: undefined, dialog: null };
}
